import React, { useCallback, useMemo } from 'react';
import { Modal } from '@dropbox/dig-components/modal';
import { Text, Title, Link } from '@dropbox/dig-components/typography';
import {
  defineMessages,
  FormattedDate,
  FormattedMessage,
  useIntl,
} from 'react-intl';
import styles from './pause.module.scss';
import { PauseCancelModalContext } from '../types';
import { RemoteWorkHero } from '@dropbox/dig-illustrations/dist/cjs/hero/remote-work';
import Spacer, { SpacerSize } from '../../../foundation/spacing/spacer';
import { LineDivider } from '../../../../hello-react/components/line-divider';
import HfReactHelper from '../../../../js/sign-components/common/hf-react-helper';
import { Select } from '@dropbox/dig-components/text_fields';
import { DurationType } from './index';

type PauseModalProps = {
  isMonthly: PauseCancelModalContext['isMonthly'];
  totalCharge: PauseCancelModalContext['totalCharge'];
  duration: string;
  handleSetDuration: (newDuration: DurationType) => void;
  nextChargeDate: string;
};

const messages = defineMessages({
  pauseDurationSelectorLabel: {
    id: '',
    defaultMessage: 'Select how long to pause your plan',
    description:
      'Accessible label for a dropdown where a customer chooses how many months to pause their plan',
  },
  oneMonthPauseDuration: {
    id: '',
    defaultMessage: '1 month',
    description: 'One-month option for how long to pause a subscription',
  },
  twoMonthPauseDuration: {
    id: '',
    defaultMessage: '2 months',
    description: 'Two-month option for how long to pause a subscription',
  },
  threeMonthPauseDuration: {
    id: '',
    defaultMessage: '3 months',
    description: 'Three-month option for how long to pause a subscription',
  },
});

const pauseHelpPageLink = HfReactHelper.getFaqUrl(
  'articles/31327622126605-Pausing-your-Dropbox-Sign-subscription',
);

const refundPolicyLink = HfReactHelper.getFaqUrl(
  'articles/115007654048-If-I-cancel-my-subscription-do-I-get-a-refund-',
);

const contactSupportLink = HfReactHelper.getFaqUrl('requests/new');

const termsLink = HfReactHelper.getDropboxSignUrl('about/terms');

const cancelSubscriptionLink = HfReactHelper.getFaqUrl(
  'articles/206074548-How-to-cancel-a-subscription-or-delete-an-account',
);

const PauseContent = ({
  isMonthly,
  totalCharge,
  duration,
  handleSetDuration,
  nextChargeDate,
}: PauseModalProps) => {
  const intl = useIntl();
  const getPauseUntilDate = useMemo(() => {
    const chargeDate = new Date(nextChargeDate);
    return new Date(chargeDate.setMonth(chargeDate.getMonth() + +duration));
  }, [nextChargeDate, duration]);

  const PauseDurationSelector = useCallback(() => {
    return (
      <div className={styles.pauseDurationContainer}>
        <FormattedMessage
          id=""
          description="Pause modal. This string is followed by a dropdown allowing the user to select a duration to pause for. Ex. Pause for 3 months"
          defaultMessage="Pause for"
        />
        <Spacer right={SpacerSize.SM} />
        <div className={styles.selector} data-qa-ref="pause-duration-selector">
          <Select
            id="pause-duration-selector"
            aria-label={intl.formatMessage(messages.pauseDurationSelectorLabel)}
            data-qa-ref="pause-duration-selector"
            value={duration}
            onChange={(selection) => {
              handleSetDuration(selection as DurationType);
            }}
          >
            <Select.Option value="1">
              {intl.formatMessage(messages.oneMonthPauseDuration)}
            </Select.Option>
            <Select.Option value="2">
              {intl.formatMessage(messages.twoMonthPauseDuration)}
            </Select.Option>
            <Select.Option value="3">
              {intl.formatMessage(messages.threeMonthPauseDuration)}
            </Select.Option>
          </Select>
        </div>
      </div>
    );
  }, [duration, handleSetDuration, intl]);

  const getHeader = useMemo(() => {
    return (
      <FormattedMessage
        id=""
        description="header, pause flow header"
        defaultMessage="Pause your plan for up to 3 months"
      />
    );
  }, []);

  const getDescription = useMemo(() => {
    return (
      <>
        <Spacer top={SpacerSize.MD} />
        <LineDivider />
        <Spacer top={SpacerSize.LG} />
        <FormattedMessage
          id=""
          description="header, pause flow header highlighting pause offer"
          defaultMessage="You'll have limited functionality and won't be charged during this time. Learn more about what pausing your account does <l1>here</l1>."
          values={{
            l1: (chunks: any[]) => (
              <Link href={pauseHelpPageLink} target="_blank">
                {chunks}
              </Link>
            ),
          }}
        />
        <Spacer top={SpacerSize.MD} />
        <Text style={{ fontStyle: 'italic' }}>
          <FormattedMessage
            id=""
            description="sub-header, pause flow header highlighting pause offer"
            defaultMessage="Billing will automatically resume on {date} and you will be billed {totalCharge} per {isMonthly, select, true {month} other {year}} starting that date and until you <l4>cancel</l4> your plan. Previous charges <l1>won't be refunded</l1> when you cancel <l2>except where required by law</l2>. You can choose to resume any time. You may <l3>contact support</l3> at any time."
            values={{
              totalCharge,
              isMonthly,
              date: (
                <FormattedDate
                  value={getPauseUntilDate}
                  day="numeric"
                  month="long"
                  year="numeric"
                />
              ),
              l1: (chunks: any[]) => (
                <Link href={termsLink} target="_blank">
                  {chunks}
                </Link>
              ),
              l2: (chunks: any[]) => (
                <Link href={refundPolicyLink} target="_blank">
                  {chunks}
                </Link>
              ),
              l3: (chunks: any[]) => (
                <Link href={contactSupportLink} target="_blank">
                  {chunks}
                </Link>
              ),
              l4: (chunks: any[]) => (
                <Link href={cancelSubscriptionLink} target="_blank">
                  {chunks}
                </Link>
              ),
            }}
          />
        </Text>
        <Spacer top={SpacerSize.LG} />
        <LineDivider />
      </>
    );
  }, [totalCharge, isMonthly, getPauseUntilDate]);

  return (
    <>
      <div className={styles.heroImage}>
        <RemoteWorkHero aspectRatio="16:9" />
      </div>
      <Modal.Header hasBottomSpacing="title-standard">
        <Title id="cancellation-flow-modal-title" className={styles.header}>
          {getHeader}
        </Title>
      </Modal.Header>
      <Modal.Body className={styles.body}>
        <PauseDurationSelector />
        <div className={styles.description}>
          <Text>{getDescription}</Text>
        </div>
      </Modal.Body>
    </>
  );
};

export default PauseContent;
